import { Target, Lightbulb, Users, Shield, TrendingUp, Rocket, Zap } from 'lucide-react';
import BackToHome from './BackToHome';

export default function About() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F0F4F3] to-white">
      <div className="container mx-auto px-6 py-12">
        <div className="mb-8">
          <BackToHome to="/" label="Back to Home" />
        </div>

        <section className="max-w-4xl mx-auto mb-16">
          <h1 className="text-5xl md:text-6xl font-semibold text-[#1F2937] mb-8 leading-tight text-center">
            About Mentra
          </h1>

          <div className="bg-white rounded-[24px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-8 md:p-12">
            <p className="text-xl text-[#4B5563] leading-relaxed mb-6">
              Mentra is revolutionizing how professionals gather and leverage feedback to accelerate their career growth and development. We're building the feedback engine for the modern workplace — a 360° growth platform that transforms everyday interactions into meaningful, actionable insights.
            </p>

            <p className="text-lg text-[#4B5563] leading-relaxed mb-6">
              Instead of outdated performance reviews or rigid HR systems, Mentra captures continuous, real-time feedback through voice, text, and seamless integrations with tools like Slack and Microsoft Teams. Our AI then distills that input into clear, personalized guidance — helping individuals and teams grow faster, with confidence and clarity.
            </p>

            <p className="text-lg text-[#4B5563] leading-relaxed font-semibold">
              Think of Mentra as your personal performance coach: automated, intelligent, and scalable across any organization. Just as Notion reinvented documentation, Mentra is redefining feedback — making it continuous, human, and beautifully simple.
            </p>
          </div>
        </section>

        <section className="max-w-4xl mx-auto mb-16">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition">
              <div className="bg-[#E0EBE9] w-14 h-14 rounded-[16px] flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-[#8CAFA8]" />
              </div>
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">Our Mission</h2>
              <p className="text-[#4B5563] leading-relaxed">
                We believe meaningful feedback is the catalyst for professional growth. Our mission is to make it effortless for professionals to collect, understand, and act on feedback that truly moves their careers forward.
              </p>
            </div>

            <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition">
              <div className="bg-green-100 w-14 h-14 rounded-[16px] flex items-center justify-center mb-6">
                <Lightbulb className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">Why We Built Mentra</h2>
              <p className="text-[#4B5563] leading-relaxed">
                Traditional feedback processes are broken — slow, stressful, inconsistent, and rarely actionable. We built Mentra to fix that. By harnessing AI, Mentra turns feedback from an awkward chore into a natural, insightful, and genuinely valuable part of everyday work.
              </p>
            </div>

            <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition">
              <div className="bg-purple-100 w-14 h-14 rounded-[16px] flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-purple-600" />
              </div>
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">Who We Serve</h2>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Mentra is built for anyone committed to continuous improvement:
              </p>
              <ul className="space-y-2 text-[#4B5563]">
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">•</span>
                  <span>Professionals accelerating their career trajectory</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">•</span>
                  <span>Managers building high-performing teams</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">•</span>
                  <span>Organizations shifting from performance review → performance enablement</span>
                </li>
              </ul>
              <p className="text-[#4B5563] leading-relaxed mt-4 font-semibold">
                If you believe feedback fuels growth, Mentra was built for you.
              </p>
            </div>

            <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition">
              <div className="bg-orange-100 w-14 h-14 rounded-[16px] flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-orange-600" />
              </div>
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">Our Commitment</h2>
              <p className="text-[#4B5563] leading-relaxed">
                Your trust is everything. We are deeply committed to protecting your privacy and securing your data. Feedback is personal — and we treat it with the care and responsibility it deserves.
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto mb-16">
          <div className="bg-gradient-to-br from-[#8CAFA8] to-[#7A9B94] rounded-[24px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-8 md:p-12 text-white">
            <div className="flex items-start gap-4 mb-8">
              <div className="flex-shrink-0">
                <div className="bg-white/20 w-14 h-14 rounded-[16px] flex items-center justify-center backdrop-blur">
                  <Zap className="w-7 h-7 text-white" strokeWidth={1.5} />
                </div>
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-semibold mb-4">How Mentra Works</h2>
                <p className="text-xl text-white/90 leading-relaxed">
                  A simple, three-step process to transform feedback into career growth
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur rounded-[16px] p-6 border border-white/20 hover:bg-white/15 transition">
                <div className="bg-white/20 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl font-semibold">1</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Request Feedback</h3>
                <p className="text-white/90 leading-relaxed">
                  Send a simple request to colleagues, managers, or mentors. They can respond with voice or text in just 5 minutes.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur rounded-[16px] p-6 border border-white/20 hover:bg-white/15 transition">
                <div className="bg-white/20 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl font-semibold">2</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">AI Analysis</h3>
                <p className="text-white/90 leading-relaxed">
                  Our AI processes responses, identifying patterns, strengths, and growth opportunities across all feedback.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur rounded-[16px] p-6 border border-white/20 hover:bg-white/15 transition">
                <div className="bg-white/20 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl font-semibold">3</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Actionable Insights</h3>
                <p className="text-white/90 leading-relaxed">
                  Receive clear, personalized recommendations to accelerate your development and advance your career.
                </p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur rounded-[16px] p-6 border border-white/20">
              <p className="text-lg text-white/90 leading-relaxed mb-4">
                <strong className="text-white">Why it works:</strong> Mentra removes the awkwardness from feedback. No more vague comments or forgotten conversations. Just continuous, meaningful insights that actually help you grow.
              </p>
              <p className="text-white/80 italic">
                Built on a freemium model — start free as an individual, upgrade to unlock team insights and advanced analytics.
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto mb-16">
          <div className="bg-white rounded-[24px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-8 md:p-12">
            <div className="flex items-start gap-4 mb-8">
              <div className="flex-shrink-0">
                <div className="bg-orange-100 w-14 h-14 rounded-[16px] flex items-center justify-center">
                  <Rocket className="w-7 h-7 text-orange-600" />
                </div>
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-semibold text-[#1F2937] mb-4">The Founders Behind the Vision</h2>
                <p className="text-lg text-[#4B5563] leading-relaxed">
                  Mentra's founding team combines speed, empathy, and world-class execution:
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#8CAFA8] rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="font-semibold text-[#1F2937] mb-1">Pattern Recognition</h4>
                  <p className="text-[#4B5563]">Multiple successful lean startups built around underserved market gaps.</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#8CAFA8] rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="font-semibold text-[#1F2937] mb-1">Full-Stack Marketing Expertise</h4>
                  <p className="text-[#4B5563]">20+ years turning deep customer insight into durable growth.</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#8CAFA8] rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="font-semibold text-[#1F2937] mb-1">Execution Under Constraints</h4>
                  <p className="text-[#4B5563]">Proven ability to ship quickly, lead hybrid teams, and secure innovation funding.</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#8CAFA8] rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="font-semibold text-[#1F2937] mb-1">User Empathy</h4>
                  <p className="text-[#4B5563]">Qualitative research roots ensure the "why" behind behavior always leads the product.</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#8CAFA8] rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="font-semibold text-[#1F2937] mb-1">A Growth Mindset</h4>
                  <p className="text-[#4B5563]">Rapid testing, fast iteration, and data-driven decision-making woven into the culture.</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#8CAFA8] rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="font-semibold text-[#1F2937] mb-1">The Bigger Vision</h4>
                  <p className="text-[#4B5563]">Mentra sits at the intersection of AI-personalized employee experiences, continuous hybrid feedback loops, and the shift from performance review to performance enablement. With a grounded go-to-market strategy and a relentless founder-led team, Mentra is redefining how organisations measure, develop, and elevate talent — continuously and intelligently.</p>
                </div>
              </div>
            </div>

            <p className="text-lg text-[#4B5563] leading-relaxed italic text-center mt-6 pt-6 border-t border-gray-200">
              This is a team of builders — operators who move fast without breaking trust.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
